import React, { useState } from 'react';
import { descriptionBullets } from '../../utils/trackingLinks';
import { MONO } from '../../constants/fonts';

const styles = {
  row: {
    display: 'flex',
    gap: 8,
  },
  input: {
    flex: 1,
    minWidth: 0,
    padding: '10px 12px',
    borderRadius: 8,
    border: '1px solid #d5d5da',
    fontSize: '0.9rem',
    boxSizing: 'border-box',
    fontFamily: 'inherit',
  },
  addButton: {
    padding: '10px 18px',
    borderRadius: 8,
    border: 'none',
    background: '#111',
    color: '#fff',
    fontSize: '0.9rem',
    fontWeight: 600,
    cursor: 'pointer',
    whiteSpace: 'nowrap',
    flexShrink: 0,
    transition: 'opacity 0.15s',
  },
  addButtonDisabled: {
    opacity: 0.5,
    cursor: 'default',
  },
  list: {
    listStyle: 'none',
    margin: '10px 0 0',
    padding: 0,
    display: 'flex',
    flexDirection: 'column',
    gap: 6,
  },
  item: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    padding: '9px 12px',
    background: '#f9f9f7',
    border: '1px solid #ececE6',
    borderRadius: 10,
    fontSize: '0.85rem',
    color: '#333',
    transition: 'opacity 0.15s',
  },
  itemDot: {
    width: 6,
    height: 6,
    borderRadius: '50%',
    background: '#34d399',
    flexShrink: 0,
  },
  itemText: {
    flex: 1,
  },
  removeButton: {
    background: 'none',
    border: 'none',
    color: '#aaa',
    fontSize: '1.2rem',
    lineHeight: 1,
    cursor: 'pointer',
    padding: '0 2px',
    flexShrink: 0,
  },
  hint: {
    fontFamily: MONO,
    fontSize: '0.7rem',
    letterSpacing: '0.04em',
    color: '#999',
    margin: '8px 0 0',
    minHeight: '1em',
  },
};

// Builds a description one item at a time (type + Add) instead of a raw
// multi-line textarea. `value`/`onChange` still work with the same
// newline-separated string the rest of the app (descriptionBullets) expects.
// `onSaveState`, when passed, receives 'saving' | 'saved' | 'error' | null
// so the caller can persist each add/remove immediately (e.g. to Firestore)
// and this component can show that state inline.
export default function DescriptionItemsInput({ value, onChange, placeholder = 'e.g. Home screen', saveState }) {
  const [draft, setDraft] = useState('');
  const items = descriptionBullets(value);
  const busy = saveState === 'saving';

  const addItem = () => {
    if (busy) return;
    const trimmed = draft.trim();
    if (!trimmed) return;
    onChange([...items, trimmed].join('\n'));
    setDraft('');
  };

  const removeItem = (index) => {
    if (busy) return;
    onChange(items.filter((_, i) => i !== index).join('\n'));
  };

  return (
    <div>
      <div style={styles.row}>
        <input
          style={styles.input}
          type="text"
          placeholder={placeholder}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              addItem();
            }
          }}
        />
        <button
          type="button"
          style={{ ...styles.addButton, ...(busy ? styles.addButtonDisabled : {}) }}
          onClick={addItem}
          disabled={busy}
        >
          Add
        </button>
      </div>
      {items.length > 0 && (
        <ul style={styles.list}>
          {items.map((item, i) => (
            <li key={i} style={{ ...styles.item, opacity: busy ? 0.6 : 1 }}>
              <span style={styles.itemDot} />
              <span style={styles.itemText}>{item}</span>
              <button
                type="button"
                style={styles.removeButton}
                onClick={() => removeItem(i)}
                disabled={busy}
                aria-label={`Remove ${item}`}
              >
                &times;
              </button>
            </li>
          ))}
        </ul>
      )}
      {saveState && (
        <p style={{ ...styles.hint, color: saveState === 'error' ? '#c0392b' : '#999' }}>
          {saveState === 'saving' && 'Saving…'}
          {saveState === 'saved' && 'Saved'}
          {saveState === 'error' && 'Failed to save — try again'}
        </p>
      )}
    </div>
  );
}
